import { useState, useEffect, useCallback } from 'react';
import { apiErrorText } from '../lib/apiError';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LifeBuoy, Search, RefreshCw, Loader2, ChevronDown, Building2, User, Clock,
  Send, Video, ShieldCheck, BellRing, Bot, Mail, Phone, Globe,
} from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BACKEND_URL } from '../config/api';
import { getAdminUserData, canManageTab } from '../utils/superAdmin';
import { Linkify, safeName } from '../lib/linkify';

const API = `${BACKEND_URL}/api/v2/super-admin/support-tickets`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

const STATUS_META = {
  open: { label: 'Open', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  in_progress: { label: 'In progress', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved: { label: 'Resolved', cls: 'bg-[#1F2A5C]/10 text-[#1F2A5C] border-[#1F2A5C]/20' },
};

const FILTERS = [
  { id: '', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'resolved', label: 'Resolved' },
];

const needsAttention = (t) => !!t.customer_pinged_at && t.status !== 'resolved';

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

/* Thread — customer/team messages + system timeline events */
const ThreadItem = ({ m }) => {
  if (m.author_type === 'system') {
    return (
      <div className="flex items-center gap-2 py-1.5 pl-1" data-testid="ticket-thread-system">
        <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-300" />
        <span className="text-[11px] leading-snug text-gray-400">{m.body}</span>
        <span className="ml-auto flex-shrink-0 text-[10px] text-gray-300">{fmtWhen(m.created_at)}</span>
      </div>
    );
  }
  const isTeam = m.author_type === 'team';
  return (
    <div className={`rounded-xl border p-3 ${isTeam ? 'border-blue-100 bg-blue-50/50' : 'border-gray-200 bg-white'}`} data-testid={`ticket-thread-${m.author_type}`}>
      <div className="flex items-center gap-2 text-[11px]">
        <span className={`font-bold uppercase tracking-wide ${isTeam ? 'text-[#2563EB]' : 'text-gray-600'}`}>
          {isTeam ? `${safeName(m.author_name, 'Team')} · BookingFlows` : safeName(m.author_name, 'Customer')}
        </span>
        <span className="ml-auto text-gray-400">{fmtWhen(m.created_at)}</span>
      </div>
      <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-gray-800"><Linkify text={m.body} /></p>
    </div>
  );
};

const TicketRow = ({ ticket, canManage, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(ticket.internal_notes || '');
  const [assignee, setAssignee] = useState(ticket.assigned_to || '');
  const [saving, setSaving] = useState(false);
  const [thread, setThread] = useState(null); // { messages, codes }
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [meetOpen, setMeetOpen] = useState(false);
  const [meetStarting, setMeetStarting] = useState(false);
  const [meetNotified, setMeetNotified] = useState(false);
  const [meetLink, setMeetLink] = useState('');
  const [meetSending, setMeetSending] = useState(false);
  const meta = STATUS_META[ticket.status] || STATUS_META.open;

  const loadThread = useCallback(() => {
    axios.get(`${API}/${ticket.id}/messages`, authHeaders())
      .then((r) => setThread(r.data))
      .catch(() => setThread({ messages: [], codes: null }));
  }, [ticket.id]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      loadThread();
      // First team view — mark viewed + notify the submitter their ticket is being reviewed
      if (!ticket.viewed_at && canManage) {
        axios.post(`${API}/${ticket.id}/viewed`, {}, authHeaders())
          .then((res) => res.data?.ticket && onUpdated(res.data.ticket, { silent: true }))
          .catch(() => {});
      }
    }
  };

  const patch = async (body, okMsg) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/${ticket.id}`, body, authHeaders());
      onUpdated(res.data.ticket);
      toast.success(okMsg);
      if (body.status) loadThread();
    } catch (e) {
      toast.error(apiErrorText(e, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return toast.error('Write a reply first');
    setSending(true);
    try {
      const res = await axios.post(`${API}/${ticket.id}/reply`, { body: reply.trim() }, authHeaders());
      if (res.data?.ticket) onUpdated(res.data.ticket, { silent: true });
      setReply('');
      loadThread();
      toast.success('Reply sent — the customer has been emailed');
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not send reply'));
    } finally {
      setSending(false);
    }
  };

  const MEET_RE = /^(https:\/\/)?meet\.google\.com\/[a-z0-9?=&_-]+$/i;
  const meetLinkValid = MEET_RE.test(meetLink.trim());

  const startMeet = async () => {
    setMeetStarting(true);
    const win = window.open('https://meet.google.com/new', '_blank', 'noopener');
    try {
      const res = await axios.post(`${API}/${ticket.id}/meet-started`, {}, authHeaders());
      if (res.data?.ticket) onUpdated(res.data.ticket, { silent: true });
      setMeetNotified(true);
      loadThread();
      toast.success('Customer notified — now paste your Meet link below');
    } catch (e) {
      if (win) win.close();
      toast.error(apiErrorText(e, 'Could not notify the customer'));
    } finally {
      setMeetStarting(false);
    }
  };

  const sendMeetLink = async () => {
    if (!meetLinkValid) return toast.error('That doesn\u2019t look like a Google Meet link');
    const normalized = meetLink.trim().startsWith('http') ? meetLink.trim() : `https://${meetLink.trim()}`;
    setMeetSending(true);
    try {
      const res = await axios.post(`${API}/${ticket.id}/reply`, { body: `Join our Google Meet here: ${normalized}` }, authHeaders());
      if (res.data?.ticket) onUpdated(res.data.ticket, { silent: true });
      setMeetOpen(false);
      setMeetNotified(false);
      setMeetLink('');
      loadThread();
      toast.success('Meet link sent — the customer has it on their ticket + email');
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not send the link'));
    } finally {
      setMeetSending(false);
    }
  };

  return (
    <div className={`rounded-2xl border bg-white ${needsAttention(ticket) ? 'border-rose-200 shadow-[0_1px_6px_rgba(244,63,94,0.08)]' : 'border-gray-200'}`} data-testid={`ticket-row-${ticket.ticket_number}`}>
      <button onClick={toggle} className="flex w-full items-center gap-3 px-4 py-3.5 text-left" data-testid={`ticket-toggle-${ticket.ticket_number}`}>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>{meta.label}</span>
        {needsAttention(ticket) && (
          <span className="flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-600" data-testid={`ticket-pinged-badge-${ticket.ticket_number}`}>
            <BellRing className="h-3 w-3" /> Customer pinged
          </span>
        )}
        {ticket.status === 'resolved' && ticket.auto_closed && (
          <span className="flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-semibold text-gray-400" data-testid={`ticket-autoclosed-${ticket.ticket_number}`}>
            <Bot className="h-3 w-3" /> Auto-closed
          </span>
        )}
        <span className="font-mono text-xs text-gray-400">{ticket.ticket_number}</span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">{ticket.subject}</span>
        <span className="hidden items-center gap-1 text-xs text-gray-500 sm:flex">
          <Building2 className="h-3.5 w-3.5" /> {ticket.org_name || ticket.org_slug || '—'}
        </span>
        <span className="hidden text-xs text-gray-400 lg:block">
          {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : ''}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-gray-100 px-4 py-4" data-testid={`ticket-detail-${ticket.ticket_number}`}>
          <div className="grid gap-3 text-xs text-gray-600 sm:grid-cols-2 lg:grid-cols-3">
            <p className="flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-gray-400" /> {safeName(ticket.user_name, 'Account owner')} · {ticket.user_role || '—'}</p>
            <p className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-gray-400" /> {ticket.org_name || '—'} ({ticket.org_slug || '—'})</p>
            <p className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gray-400" /> {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : '—'}</p>
          </div>
          {thread?.contact && (thread.contact.email || thread.contact.phone || thread.contact.website) && (
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-2.5 text-xs text-gray-600" data-testid={`ticket-contact-${ticket.ticket_number}`}>
              {thread.contact.email && (
                <a href={`mailto:${thread.contact.email}`} className="flex items-center gap-1.5 transition-colors hover:text-[#2563EB]"><Mail className="h-3.5 w-3.5 text-gray-400" /> {thread.contact.email}</a>
              )}
              {thread.contact.phone && (
                <a href={`tel:${thread.contact.phone}`} className="flex items-center gap-1.5 transition-colors hover:text-[#2563EB]"><Phone className="h-3.5 w-3.5 text-gray-400" /> {thread.contact.phone}</a>
              )}
              {thread.contact.website && (
                <a href={thread.contact.website.startsWith('http') ? thread.contact.website : `https://${thread.contact.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 transition-colors hover:text-[#2563EB]"><Globe className="h-3.5 w-3.5 text-gray-400" /> {thread.contact.website}</a>
              )}
              <span className="text-[10px] uppercase tracking-wide text-gray-400">Calling? Verify with the secure-line codes below</span>
            </div>
          )}
          <p className="mt-3 rounded-xl bg-gray-50 p-3 text-xs text-gray-500 font-semibold uppercase tracking-wide">Category: {ticket.category || '—'}</p>
          <p className="mt-3 whitespace-pre-wrap rounded-xl bg-gray-50 p-4 text-sm leading-relaxed text-gray-800" data-testid={`ticket-description-${ticket.ticket_number}`}>
            {ticket.description}
          </p>

          {/* ── Conversation & timeline ── */}
          <div className="mt-4" data-testid={`ticket-thread-${ticket.ticket_number}`}>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">Conversation & timeline</p>
            {thread === null ? (
              <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-gray-300" /></div>
            ) : thread.messages.length === 0 ? (
              <p className="text-xs text-gray-400">No activity yet beyond the original request.</p>
            ) : (
              <div className="space-y-2">
                {thread.messages.map((m) => <ThreadItem key={m.id} m={m} />)}
              </div>
            )}
          </div>

          {canManage && (
            <>
              {ticket.status === 'resolved' ? (
                <p className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500" data-testid={`ticket-resolved-note-${ticket.ticket_number}`}>
                  This ticket is resolved — the conversation is closed on both sides. Set it back to Open or In progress to continue.
                </p>
              ) : (
              <div className="mt-4">
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-400">Reply to the customer (sent by email + visible on their ticket)</label>
                <Textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Hi — thanks for flagging this. Here's what we found…"
                  data-testid={`ticket-reply-input-${ticket.ticket_number}`}
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    onClick={sendReply}
                    disabled={sending}
                    className="flex items-center gap-1.5 rounded-lg bg-[#2563EB] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#1d4fc4] disabled:opacity-50"
                    data-testid={`ticket-reply-send-${ticket.ticket_number}`}
                  >
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send reply
                  </button>
                  <button
                    onClick={() => setMeetOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                    data-testid={`ticket-meet-btn-${ticket.ticket_number}`}
                  >
                    <Video className="h-3.5 w-3.5 text-[#2563EB]" /> Start Google Meet
                  </button>
                </div>
              </div>
              )}

              {meetOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" data-testid={`ticket-meet-modal-${ticket.ticket_number}`}>
                  <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                    <h3 className="flex items-center gap-2 text-base font-bold text-gray-900">
                      <Video className="h-4 w-4 text-[#2563EB]" /> Start a Google Meet
                    </h3>
                    {!meetNotified ? (
                      <>
                        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-gray-600">
                          <li>We&rsquo;ll notify <strong>{safeName(ticket.user_name, 'the customer')}</strong> — by email and with a note on their ticket — that a Meet is starting.</li>
                          <li>A new tab opens at <strong>meet.google.com/new</strong> — use your BookingFlows Google account, then <strong>copy the meeting link</strong> from the address bar.</li>
                          <li>Come back here and <strong>paste the link into the box we&rsquo;ll show you</strong> — we&rsquo;ll send it straight to the customer.</li>
                        </ol>
                        <div className="mt-5 flex justify-end gap-2">
                          <button
                            onClick={() => setMeetOpen(false)}
                            className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                            data-testid={`ticket-meet-cancel-${ticket.ticket_number}`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={startMeet}
                            disabled={meetStarting}
                            className="flex items-center gap-1.5 rounded-lg bg-[#2563EB] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1d4fc4] disabled:opacity-50"
                            data-testid={`ticket-meet-confirm-${ticket.ticket_number}`}
                          >
                            {meetStarting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Video className="h-3.5 w-3.5" />} Notify customer & open Meet
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="mt-2 text-sm leading-relaxed text-gray-600">
                          Customer notified ✓ &nbsp;Now <strong>paste your Google Meet link here</strong> — copy it from the address bar of the Meet tab we just opened:
                        </p>
                        <Input
                          className="mt-3 h-11 font-mono text-sm"
                          placeholder="https://meet.google.com/xxx-xxxx-xxx"
                          value={meetLink}
                          onChange={(e) => setMeetLink(e.target.value)}
                          data-testid={`ticket-meet-link-input-${ticket.ticket_number}`}
                        />
                        {meetLink.trim() && !meetLinkValid && (
                          <p className="mt-1.5 text-[11px] font-semibold text-rose-500" data-testid={`ticket-meet-link-error-${ticket.ticket_number}`}>
                            Must be a meet.google.com link — e.g. https://meet.google.com/ixe-rucx-cje
                          </p>
                        )}
                        <div className="mt-4 flex justify-end gap-2">
                          <button
                            onClick={() => { setMeetOpen(false); setMeetNotified(false); setMeetLink(''); }}
                            className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                            data-testid={`ticket-meet-skip-${ticket.ticket_number}`}
                          >
                            I&rsquo;ll paste it in a reply instead
                          </button>
                          <button
                            onClick={sendMeetLink}
                            disabled={!meetLinkValid || meetSending}
                            className="flex items-center gap-1.5 rounded-lg bg-[#2563EB] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1d4fc4] disabled:opacity-50"
                            data-testid={`ticket-meet-send-link-${ticket.ticket_number}`}
                          >
                            {meetSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send link to customer
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* ── Secure-line codes ── */}
              {thread?.codes && (
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3" data-testid={`ticket-codes-${ticket.ticket_number}`}>
                  <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Secure line
                  </span>
                  <span className="text-xs text-gray-600">Your agent code <strong className="font-mono text-sm text-gray-900">{thread.codes.agent_code}</strong></span>
                  <span className="text-xs text-gray-600">Customer&rsquo;s code <strong className="font-mono text-sm text-gray-900">{thread.codes.customer_code}</strong></span>
                  <span className="basis-full text-[11px] text-gray-400">Calling them? Read your agent code first, then ask for theirs — both are shown on their ticket in the Help Center.</span>
                </div>
              )}

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-400">Status</label>
                  <Select value={ticket.status} onValueChange={(v) => patch({ status: v }, `Marked ${STATUS_META[v]?.label?.toLowerCase()}`)}>
                    <SelectTrigger className="h-10" data-testid={`ticket-status-select-${ticket.ticket_number}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-400">Assigned to</label>
                  <div className="flex gap-2">
                    <Input className="h-10" placeholder="e.g. Khadija" value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid={`ticket-assignee-input-${ticket.ticket_number}`} />
                    <button
                      onClick={() => patch({ assigned_to: assignee }, 'Assignee saved')}
                      disabled={saving}
                      className="rounded-lg bg-[#1F2A5C] px-3 text-xs font-semibold text-white transition-colors hover:bg-[#28356f] disabled:opacity-50"
                      data-testid={`ticket-assignee-save-${ticket.ticket_number}`}
                    >
                      Save
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-400">Internal notes (never shown to the customer)</label>
                  <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`ticket-notes-input-${ticket.ticket_number}`} />
                  <button
                    onClick={() => patch({ internal_notes: notes }, 'Notes saved')}
                    disabled={saving}
                    className="mt-2 rounded-lg bg-[#1F2A5C] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#28356f] disabled:opacity-50"
                    data-testid={`ticket-notes-save-${ticket.ticket_number}`}
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save notes'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const SuperAdminTicketsTab = () => {
  const userData = getAdminUserData();
  const canManage = canManageTab(userData, 'tickets');
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, resolved: 0, total: 0 });
  const [filter, setFilter] = useState('open');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const PAGE_SIZE = 50;
  const [visible, setVisible] = useState(PAGE_SIZE);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set('status', filter);
      if (q.trim()) params.set('q', q.trim());
      params.set('limit', '500');
      const res = await axios.get(`${API}?${params.toString()}`, authHeaders());
      setTickets(res.data.tickets || []);
      setCounts(res.data.counts || {});
      setVisible(PAGE_SIZE);
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not load tickets'));
    } finally {
      setLoading(false);
    }
  }, [filter, q]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const onUpdated = (updated, opts = {}) => {
    setTickets((ts) => ts.map((t) => (t.id === updated.id ? updated : t)));
    if (!opts.silent) fetchTickets();
  };

  return (
    <div data-testid="superadmin-tickets-tab">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <LifeBuoy className="h-5 w-5 text-[#2563EB]" /> Support Tickets
          </h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Tickets submitted from the Help Center. Quiet tickets are auto-nudged at 5 &amp; 10 days and auto-closed at 13.
          </p>
        </div>
        <button onClick={fetchTickets} className="flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50" data-testid="tickets-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => {
          const count = f.id === '' ? counts.total : counts[f.id];
          return (
            <button
              key={f.id || 'all'}
              onClick={() => setFilter(f.id)}
              data-testid={`tickets-filter-${f.id || 'all'}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                filter === f.id ? 'bg-[#1F2A5C] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}{typeof count === 'number' ? ` (${count})` : ''}
            </button>
          );
        })}
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            className="h-9 w-56 pl-9"
            placeholder="Search tickets…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="tickets-search-input"
          />
        </div>
      </div>

      {loading && tickets.length === 0 ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-300" /></div>
      ) : tickets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center" data-testid="tickets-empty">
          <LifeBuoy className="mx-auto h-8 w-8 text-gray-300" />
          <p className="mt-3 text-sm font-semibold text-gray-700">No {filter ? STATUS_META[filter]?.label?.toLowerCase() : ''} tickets</p>
          <p className="mt-1 text-xs text-gray-500">New tickets from the Help Center will appear here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.slice(0, visible).map((t) => (
            <TicketRow key={t.id} ticket={t} canManage={canManage} onUpdated={onUpdated} />
          ))}
          {tickets.length > visible && (
            <button
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              data-testid="tickets-load-more"
              className="w-full rounded-xl border border-gray-200 bg-white py-3 text-xs font-semibold text-[#1F2A5C] transition-colors hover:bg-gray-50"
            >
              Show {Math.min(PAGE_SIZE, tickets.length - visible)} more ({tickets.length - visible} remaining)
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAdminTicketsTab;
